import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Mail,
  MailOutline,
  ExpandMore,
  ExpandLess,
  AttachFile,
  Download,
} from '@mui/icons-material';
import { BRAND_COLORS } from '../../../styles/theme';
import { Announcement } from '../../../types/api';
import { useTranslation } from '../../../i18n';
import { formatDateTimeISO } from '../../../utils/dateUtils';
import { useAnnouncementsQuery } from './dashboard.queries';
import {
  useMarkAnnouncementReadMutation,
  useDownloadAnnouncementAttachmentMutation,
} from './dashboard.mutations';
import { useQueryClient } from '@tanstack/react-query';
import { dashboardKeys } from './dashboard.queries';

interface InboxModalProps {
  open: boolean;
  onClose: () => void;
}

export const InboxModal: React.FC<InboxModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [downloadingAttachment, setDownloadingAttachment] = useState<string | null>(null);

  // Local optimistic state for read status while server call is in flight
  const [localReadIds, setLocalReadIds] = useState<Set<string>>(new Set());

  const { data: announcements = [], isLoading: loading, isError } = useAnnouncementsQuery(open);

  const markReadMutation = useMarkAnnouncementReadMutation();
  const downloadAttachmentMutation = useDownloadAnnouncementAttachmentMutation();

  const handleToggleExpand = async (announcement: Announcement) => {
    const isExpanding = expandedId !== announcement.id;
    setExpandedId(isExpanding ? announcement.id : null);

    // Mark as read when expanding
    if (isExpanding && !announcement.is_read && !localReadIds.has(announcement.id)) {
      // Optimistic update
      setLocalReadIds((prev) => new Set(prev).add(announcement.id));
      markReadMutation.mutate(announcement.recipient_id, {
        onSuccess: () => {
          // Invalidate the announcements list so the query refetches with server state
          queryClient.invalidateQueries({ queryKey: dashboardKeys.announcements() });
        },
        onError: () => {
          // Revert optimistic update
          setLocalReadIds((prev) => {
            const next = new Set(prev);
            next.delete(announcement.id);
            return next;
          });
        },
      });
    }
  };

  const handleDownloadAttachment = (e: React.MouseEvent, s3Key: string, filename: string) => {
    e.stopPropagation(); // Prevent collapse toggle

    setDownloadingAttachment(s3Key);
    downloadAttachmentMutation.mutate(s3Key, {
      onSuccess: (url) => {
        window.open(url, '_blank');
        setDownloadingAttachment(null);
      },
      onError: () => {
        setDownloadingAttachment(null);
        // Error toast is handled globally; filename is used in the meta fallback
        console.error(`Failed to download ${filename}`);
      },
    });
  };

  // Merge server is_read with local optimistic updates
  const displayAnnouncements = announcements.map((a) => ({
    ...a,
    is_read: a.is_read || localReadIds.has(a.id),
  }));

  const unreadCount = displayAnnouncements.filter((a) => !a.is_read).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Mail />
            <Typography variant="h6">{t('inbox.title')}</Typography>
          </Box>
          {unreadCount > 0 && <Chip label={`${unreadCount}`} color="error" size="small" />}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error">{t('inbox.failedToLoad')}</Alert>
        ) : displayAnnouncements.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <MailOutline sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {t('inbox.noMessages')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {displayAnnouncements.map((announcement) => (
              <Paper
                key={announcement.id}
                sx={{
                  p: 2,
                  backgroundColor: announcement.is_read ? 'white' : '#f5f5f5',
                  border: announcement.is_read
                    ? '1px solid #e0e0e0'
                    : `2px solid ${BRAND_COLORS.yellow.main}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 2,
                  },
                }}
                onClick={() => handleToggleExpand(announcement)}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {announcement.is_read ? (
                        <MailOutline sx={{ fontSize: 20, color: 'text.secondary' }} />
                      ) : (
                        <Mail sx={{ fontSize: 20, color: BRAND_COLORS.forestGreen.main }} />
                      )}
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: announcement.is_read ? 400 : 600,
                          color: announcement.is_read ? 'text.secondary' : 'text.primary',
                        }}
                      >
                        {announcement.title}
                      </Typography>
                      {!announcement.is_read && (
                        <Chip
                          label="NEW"
                          size="small"
                          color="error"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      From: {announcement.created_by.name || announcement.created_by.email} •{' '}
                      {formatDateTimeISO(announcement.created_at)}
                    </Typography>
                  </Box>
                  <IconButton size="small">
                    {expandedId === announcement.id ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedId === announcement.id}>
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                      {announcement.body}
                    </Typography>

                    {announcement.attachments && announcement.attachments.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
                        >
                          <AttachFile fontSize="small" />
                          Attachments ({announcement.attachments.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {announcement.attachments.map((attachment, index) => (
                            <Paper
                              key={index}
                              sx={{
                                p: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor: '#fafafa',
                              }}
                            >
                              <Box>
                                <Typography variant="body2">{attachment.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {(attachment.size / 1024).toFixed(1)} KB
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={(e) =>
                                  handleDownloadAttachment(e, attachment.s3_key, attachment.name)
                                }
                                disabled={downloadingAttachment === attachment.s3_key}
                              >
                                {downloadingAttachment === attachment.s3_key ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Download />
                                )}
                              </IconButton>
                            </Paper>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {markReadMutation.isPending && expandedId === announcement.id && (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                          Marking as read...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: BRAND_COLORS.forestGreen.main,
            '&:hover': {
              backgroundColor: BRAND_COLORS.forestGreen.main,
              opacity: 0.8,
            },
          }}
        >
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
